import SamplerVoice from './SamplerVoice.js';
import Tone from 'tone';

class SamplerVoices {
    constructor(numVoices, outputNode, sampledInstrument) {
        this.sampledInstrument = sampledInstrument;

        this.voices = [];

        this.outputNode = new Tone.Gain(1);
        for (let i=0; i < numVoices; i++) {
            let voice = new SamplerVoice(i, sampledInstrument);
            voice.connect(this.outputNode);
            this.voices.push(voice);
        }

        this.unusedVoices = [... this.voices];

        this.outputNode.connect(outputNode);

        this.numVoices = numVoices;

        this.samplersUsed = new Set();
    }

    getVoices() {
        return this.numVoices === 1 ? [this.voices[0]] : this.voices;
    }

    clear() {
        this.voices.forEach(s => s.clear());
        this.samplersUsed = new Set();
    }

    dispose() {
        this.voices.forEach(v => v.dispose());
        this.outputNode.dispose();
        this.voices = undefined;
        this.outputNode = undefined;
    }

    get ampEnvelopes() {
        return this.voices.map(v => v.ampEnvelope);
    }

    getUnusedVoiceIfAny(sampler) {
        if (this.numVoices === 1) {
            return this.voices[0];
        }
        if (this.unusedVoices.length === 0) {
            return this.voices[Math.floor(Math.random() * this.numVoices)];
        }
        return this.unusedVoices.shift();
    }

    getVoiceUsingSampler(sampler, transpose) {
        return this.voices.find(
            v =>
                v.samplerToTriggers[sampler.id] &&
                v.samplerToTriggers[sampler.id][transpose]);
    }

    useSamplerWithVoice(sampler, voice, time, transpose, loopPoints) {
        voice.useSampler(sampler, time, transpose, loopPoints);
        this.samplersUsed.add(sampler);
    }

    disconnectSamplerFromVoice(sampler, voice, transpose) {
        if (voice.disconnectSampler(sampler, transpose)) {
            if (!voice.isUsed()) {
                this.unusedVoices.push(voice);
                this.samplersUsed.delete(sampler);
            } 
        }
    }

    async triggerAttack(sampler, transpose, time, velocity, loopPoints, pitchEnvelope) {
        let voice = this.getUnusedVoiceIfAny(sampler);
        this.useSamplerWithVoice(sampler, voice, time, transpose, loopPoints);
        voice.ampEnvelope.triggerAttack(time, velocity);

        let promises = [
            sampler.triggerAttack(
                transpose,
                time,
                velocity,
                loopPoints,
                pitchEnvelope,
                voice.crossFade.input[0],
                false)]; // original
        if (this.sampledInstrument.crossFadeParameter.get() > 0) { 
            promises.push(
                sampler.triggerAttack(
                    transpose,
                    time,
                    velocity,
                    loopPoints,
                    pitchEnvelope,
                    voice.crossFade.input[1],
                    true));
        }
        await Promise.all(promises);


        if (!sampler.loop) {
            this.disconnectSamplerFromVoice(sampler, voice, transpose);
        }
    }

    async triggerAttackRelease(sampler, transpose, duration, time, velocity, loopPoints, pitchEnvelope, start) {
        let voice = this.getUnusedVoiceIfAny(sampler);
        this.useSamplerWithVoice(sampler, voice, time, transpose, loopPoints);
        voice.ampEnvelope.triggerAttackRelease(duration, time, velocity, loopPoints);
        let promises = [
            sampler.triggerAttackRelease(
                transpose,
                duration,
                time,
                velocity,
                loopPoints,
                pitchEnvelope,
                voice.crossFade.input[0],
                start,
                false)]; // original
        if (this.sampledInstrument.crossFadeParameter.get() > 0) {
            promises.push(
                sampler.triggerAttackRelease(
                    transpose,
                    duration,
                    time,
                    velocity,
                    loopPoints,
                    pitchEnvelope,
                    voice.crossFade.input[1],
                start,
                    true)); // reversed
        }
        await Promise.all(promises);

        voice.onVoiceRelease(sampler, transpose);
        this.disconnectSamplerFromVoice(sampler, voice, transpose);
    }

    async triggerRelease(sampler, notes) {
        sampler.triggerRelease(notes, false); // original
        if (this.sampledInstrument.crossFadeParameter.get() > 0) {
            sampler.triggerRelease(notes, true); // reversed
        }
        let voice = this.getVoiceUsingSampler(sampler, notes[0]);
        if (voice === undefined) {
            return;
        }
        if (voice.totalTriggers <= 1) {
            voice.ampEnvelope.triggerRelease(Tone.Master.context.currentTime);
        }
        voice.onVoiceRelease(sampler, notes[0]);
    }

    setCrossFade(cross) {
        this.voices.forEach(
            s => cross.connect(s.crossFade.fade));
    }
}

export default SamplerVoices;
